import React, { useState, useRef, useEffect, useMemo } from "react";

// ─── PETER — FLOATING CHAT WIDGET (with lead capture) ─────────────────────────
// Drop-in replacement for the previous PeterQAWidget — same import path, same
// default export — but now a floating bottom-right chat launcher instead of a
// static browsable list, gated by a short lead form so every conversation is
// tied to a name/email/phone before it starts.
//
// THEME: auto-detects the site's dark mode by watching for the `dark` class
// App.jsx already applies to <html> (see the toggleTheme/useEffect in your
// App.jsx). No separate dark-mode toggle inside the widget — it just follows
// the site. You can still force a mode by passing isDarkMode explicitly, e.g.
// <PeterQAWidget isDarkMode={theme === "dark"} />, which overrides auto-detection.
//
// LEAD CAPTURE + LOGGING: stored to localStorage on this device so a returning
// visitor isn't asked twice, and every question asked is logged locally too.
// Both are wired through two small stub functions — submitLead() and
// logInteraction() — search for "BACKEND HOOK" below to point them at your
// real API once one exists. Nothing here assumes a backend that doesn't exist yet.

const SUPPORT_EMAIL = "support@pilotlinkusa.com"; // ← replace with your real support inbox
const LEAD_STORAGE_KEY = "peter_lead_v1";
const LOG_STORAGE_KEY = "peter_question_log_v1";

const QA_DATA = [
  {
    "category": "About PilotLink USA",
    "question": "What is PilotLink USA?",
    "answer": "PilotLink USA is a platform for oversize load pilot car (escort) professionals. We handle certification training, a load board, a verified driver marketplace, and the compliance tools you need to work legally across the country — all in one place.",
    "escalate": false
  },
  {
    "category": "About PilotLink USA",
    "question": "Who is this platform for?",
    "answer": "Anyone working in the pilot car / oversize load escort industry: independent drivers getting certified for the first time, experienced pilot car operators, escort companies hiring drivers, and dispatchers coordinating loads.",
    "escalate": false
  },
  {
    "category": "About PilotLink USA",
    "question": "Do I need any experience to get started?",
    "answer": "No. Our Pilot Car Starter Guide is built specifically for people with zero experience — it covers equipment, state basics, CB radio protocol, reading permits, and finding your first load.",
    "escalate": false
  },
  {
    "category": "About PilotLink USA",
    "question": "What's the difference between a Driver, Company, and Dispatcher account?",
    "answer": "Driver accounts are for individual pilot car operators getting certified and finding loads. Company accounts are for escort businesses hiring and managing drivers. Dispatcher accounts are for coordinating loads and communications between companies and drivers.",
    "escalate": false
  },
  {
    "category": "About PilotLink USA",
    "question": "Is PilotLink USA available in every state?",
    "answer": "Yes — our certification map and courses cover all 50 states plus DC, whether that state requires certification, is part of the 13-state reciprocity network, or requires its own independent exam.",
    "escalate": false
  },
  {
    "category": "About PilotLink USA",
    "question": "Is PilotLink USA affiliated with any state DOT?",
    "answer": "No. We provide training and compliance tools, but we don't issue state licenses ourselves. You're always responsible for confirming current requirements with your state's official DOT.",
    "escalate": false
  },
  {
    "category": "About PilotLink USA",
    "question": "How is PilotLink USA different from just Googling my state's rules?",
    "answer": "State rules change and are scattered across dozens of DOT sites. We centralize verified requirements, keep Cheat Sheets updated, and pair that with structured courses, practice exams, and a Verified Badge companies can check in seconds.",
    "escalate": false
  },
  {
    "category": "About PilotLink USA",
    "question": "Can I use PilotLink USA on my phone?",
    "answer": "Yes, the site is fully responsive and works on phone, tablet, or desktop, in both light and dark mode.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "What is a \"pilot car\" or escort vehicle certification?",
    "answer": "It's proof that you've been trained on the rules for escorting oversize/overweight loads — things like following distance, signage, height-pole use, and communication with the truck driver — so you can legally work as an escort in states that require it.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "What's PPCA?",
    "answer": "PPCA (Professional Pilot Car Association certification path) is our master certification — it covers the 13-state reciprocity network, the 3 independent-certification states, and the escort/equipment rules for the remaining states that don't require an exam, all in one course.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "What's PEVO?",
    "answer": "PEVO stands for Pilot/Escort Vehicle Operator — the foundational training on core escort procedures, equipment setup, and radio protocol that most certification paths build on.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "What is the 13-State Reciprocity Network?",
    "answer": "It's a group of 13 states that all accept one shared certification instead of requiring their own separate exam. Pass the network course once and you're certified to work in all 13.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "Which 13 states are in the reciprocity network?",
    "answer": "Arizona, Colorado, Florida, Georgia, Kansas, Minnesota, North Carolina, Oklahoma, Pennsylvania, Texas, Utah, Virginia, and Washington.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "What are the \"independent certification\" states?",
    "answer": "New York, Oregon, and South Dakota. Each requires its own separate state-specific certification — none of them accept the 13-state network credential, and they don't accept each other's either.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "Do all states require certification?",
    "answer": "No. Only 13 states (the reciprocity network) plus New York, Oregon, and South Dakota require a formal certification exam. The remaining states don't require a certification exam by law, though escort trigger dimensions, insurance minimums, and equipment rules still apply.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "How many states require no certification at all?",
    "answer": "35 states plus DC don't require a certification exam by law — but you still need to follow that state's escort trigger rules, insurance minimums, and equipment requirements.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "If my state doesn't require certification, do I need to do anything?",
    "answer": "You don't need to pass an exam, but you should still know that state's escort trigger dimensions, insurance minimums, and equipment rules — our PPCA Master course and Cheat Sheets cover all of that.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "I already have a certification from another company. Do I need to start over?",
    "answer": "Not necessarily. Head to PPCA & Verified Badge → Verification Center, upload your existing certification, and pass a short rules quiz to confirm you're current — no full course required.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "How long does it take to get certified?",
    "answer": "It depends on the course — our starter course runs about 2 hours, while the full PPCA Master Certification is 6–8 hours across 12 modules. You can move at your own pace.",
    "escalate": false
  },
  {
    "category": "Certification Basics",
    "question": "Can I fail the exam?",
    "answer": "Yes, but there's no penalty — you can retake any exam as many times as needed until you pass, at no extra charge.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "What courses does PilotLink USA offer?",
    "answer": "Six courses: PPCA Master Certification, the Complete Bundle — Everything, the Pilot Car Starter Guide, 13-State Reciprocity Certification, Independent States Certification Pack, and Pilot Car Business Starter.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "What is the PPCA Master Certification course?",
    "answer": "Our most complete single course — it covers the 13-state reciprocity network, the 3 independent-certification states, and every no-cert-required state's rules, so you're prepared for a job anywhere in the country.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "What is the \"Complete Bundle — Everything\"?",
    "answer": "It's every course on the platform in one purchase — PPCA Master, 13-State Reciprocity, the Independent States Pack, the Starter Guide, and the Business Starter course — plus all future course updates at no extra charge.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "What is the Pilot Car Starter Guide?",
    "answer": "A beginner-focused course covering equipment, state basics, CB radio protocol, reading oversize permits, and finding your first load — ideal if you're brand new to the industry.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "What is the 13-State Reciprocity Certification course?",
    "answer": "A focused course that certifies you across all 13 network states with a single credential — the most efficient path if you mainly work within that network.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "What is the Independent States Certification Pack?",
    "answer": "It prepares you individually for New York, Oregon, and South Dakota's separate state-specific exams, since none of them accept the network certification.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "What is the Pilot Car Business Starter course?",
    "answer": "A business-focused course for certified operators who want to run their own pilot car business — LLC setup, insurance buying, pricing your services, and invoicing.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "How much do the courses cost?",
    "answer": "Pricing varies by course, from around $49 for the Starter Guide up to $499 for the Complete Bundle — Everything. Exact current pricing is always shown on each course card on the Online Courses tab.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "Is it a subscription or a one-time payment?",
    "answer": "Course purchases are a one-time payment, not a subscription — no recurring charges for the course itself.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "Do I get permanent access after I pay once?",
    "answer": "You get full access to the course content you purchased. For State Exam courses specifically, ongoing access to the Crash Course refresher material is tied to your active Learning Cycle, which depends on keeping your state certification current — so it's worth checking the Certification Policy section for the details that apply to your course.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "Can I buy just one course, or do I have to buy a bundle?",
    "answer": "You can buy any single course on its own — bundles are just there if you want to save money by getting more than one course at once.",
    "escalate": false
  },
  {
    "category": "Courses & Pricing",
    "question": "Do bundle prices ever change?",
    "answer": "Pricing and discounts can be updated from time to time. Always check the current price shown on the course card before enrolling — that's the price you'll pay.",
    "escalate": false
  },
  {
    "category": "PPCA & Verified Badge",
    "question": "What happens after I pass the PPCA exam?",
    "answer": "Your results are automatically transmitted to the Platform, and a Verified PPCA Badge — including a unique QR code — is issued and attached to your profile right away.",
    "escalate": false
  },
  {
    "category": "PPCA & Verified Badge",
    "question": "What is the Verified Badge?",
    "answer": "It's the credential companies search for on PilotLink USA. It shows on your profile with a scannable QR code so employers can instantly confirm you're certified.",
    "escalate": false
  },
  {
    "category": "PPCA & Verified Badge",
    "question": "What's the QR code on my badge for?",
    "answer": "It lets employers verify your certification instantly by scanning it — no need to request documents or wait for confirmation.",
    "escalate": false
  },
  {
    "category": "PPCA & Verified Badge",
    "question": "What if I fail the PPCA exam?",
    "answer": "You can retake it as many times as needed until you pass — there's no limit and no extra charge. If you've completed 100% of the required modules, your course access can even be reset for free.",
    "escalate": false
  },
  {
    "category": "PPCA & Verified Badge",
    "question": "Is there a limit to how many times I can retake the PPCA exam?",
    "answer": "No limit — retake it until you pass.",
    "escalate": false
  },
  {
    "category": "PPCA & Verified Badge",
    "question": "I already have a cert from elsewhere — can I skip the full course?",
    "answer": "Yes. Upload your existing certification in the Verification Center and pass a short rules quiz confirming you're current — your Verified Badge unlocks without taking the full course.",
    "escalate": false
  },
  {
    "category": "PPCA & Verified Badge",
    "question": "What is the Verification Center / quick rules quiz?",
    "answer": "It's for drivers who already hold a valid network or state certification. Instead of the full course, you upload proof and pass a short quiz on current rules to earn your Verified Badge.",
    "escalate": false
  },
  {
    "category": "PPCA & Verified Badge",
    "question": "Do I need to retake anything after I pass the PPCA exam?",
    "answer": "No. Once your PPCA badge is issued, your obligations are fully satisfied — no further quiz retakes are required.",
    "escalate": false
  },
  {
    "category": "PPCA & Verified Badge",
    "question": "What's the Pass Guarantee?",
    "answer": "If you complete 100% of a course's content before your PPCA or State Exam and don't pass, you can retake the exam for free — no questions asked.",
    "escalate": false
  },
  {
    "category": "PPCA & Verified Badge",
    "question": "Can I get my money back if I fail?",
    "answer": "The Pass Guarantee covers free retakes, but PilotLink USA does not provide monetary refunds under any circumstances.",
    "escalate": false
  },
  {
    "category": "State Requirements",
    "question": "How do I know what my state requires?",
    "answer": "Use the certification map on the Overview & Map tab — click your state to see whether it's in the 13-state network, requires its own independent certification, or needs no exam at all.",
    "escalate": false
  },
  {
    "category": "State Requirements",
    "question": "What do the colors on the certification map mean?",
    "answer": "Blue/teal = the 13-state reciprocity network, orange/amber = independent certification required (NY, OR, SD), and gray = no certification required by law.",
    "escalate": false
  },
  {
    "category": "State Requirements",
    "question": "My state isn't listed/colored — what does that mean?",
    "answer": "If a state doesn't show a special color, it falls in the 'no certification required' group — but escort trigger rules, insurance minimums, and equipment requirements can still apply, so it's worth checking that state's note on the map.",
    "escalate": false
  },
  {
    "category": "State Requirements",
    "question": "Does New York accept the 13-state reciprocity certification?",
    "answer": "No. New York has zero reciprocity — it doesn't accept any other state's certification, and no other state accepts New York's.",
    "escalate": false
  },
  {
    "category": "State Requirements",
    "question": "Do Oregon and South Dakota accept the network certification?",
    "answer": "No, both require their own separate state-specific certification, just like New York.",
    "escalate": false
  },
  {
    "category": "State Requirements",
    "question": "I work across multiple states — which course do I need?",
    "answer": "If you mainly work within the 13-state network, the 13-State Reciprocity Certification is enough. If you also work in NY, OR, or SD, add the Independent States Certification Pack, or get everything at once with PPCA Master Certification.",
    "escalate": false
  },
  {
    "category": "State Requirements",
    "question": "What if my state's requirements change after I get certified?",
    "answer": "We monitor state regulations and update Crash Course content to reflect current requirements. If you're in an active Learning Cycle, those updates are included automatically.",
    "escalate": false
  },
  {
    "category": "State Requirements",
    "question": "Are insurance minimums part of certification?",
    "answer": "Insurance minimums are a separate state requirement from certification itself, but we cover them for each state — for example, several network states require a $500,000–$1,000,000 CSL minimum.",
    "escalate": false
  },
  {
    "category": "State Requirements",
    "question": "Do state requirements ever conflict with each other?",
    "answer": "Occasionally — for example, some states accept the network certification only in one direction (like Pennsylvania and Florida). Each state's note on the map calls out these exceptions clearly.",
    "escalate": false
  },
  {
    "category": "State Requirements",
    "question": "Where can I find state-specific equipment and escort trigger rules?",
    "answer": "In the Cheat Sheets (for members) and in each state's detail note on the certification map under All State Certs.",
    "escalate": false
  },
  {
    "category": "Cheat Sheets",
    "question": "What are Cheat Sheets?",
    "answer": "Quick-reference guides covering each state's vehicle rules, permit rules, escort trigger thresholds, local codes, and official DOT links — built for fast lookups in the field.",
    "escalate": false
  },
  {
    "category": "Cheat Sheets",
    "question": "Do I need to pay to access Cheat Sheets?",
    "answer": "Yes, Cheat Sheets are a members-only feature. Join as a member to unlock the full library.",
    "escalate": false
  },
  {
    "category": "Cheat Sheets",
    "question": "Can I download or print the Cheat Sheets?",
    "answer": "No — Cheat Sheets are login-protected and can't be downloaded or copied, so they always reflect the most current information when you view them.",
    "escalate": false
  },
  {
    "category": "Cheat Sheets",
    "question": "What's included in a state Cheat Sheet?",
    "answer": "Vehicle rules, permit rules, escort threshold dimensions, local codes, and links to official DOT resources for that state.",
    "escalate": false
  },
  {
    "category": "Cheat Sheets",
    "question": "How often are Cheat Sheets updated?",
    "answer": "They're updated whenever state regulations change, so members always see current requirements rather than outdated PDFs.",
    "escalate": false
  },
  {
    "category": "Cheat Sheets",
    "question": "Can I access Cheat Sheets without being certified?",
    "answer": "Yes — Cheat Sheet access is tied to membership, not certification. You don't need PPCA or a state exam to become a member and view them.",
    "escalate": false
  },
  {
    "category": "Membership & Pricing",
    "question": "Do I need to be certified to become a member?",
    "answer": "No. Membership is separate from certification — you can join without taking any exam.",
    "escalate": false
  },
  {
    "category": "Membership & Pricing",
    "question": "What membership plans are available?",
    "answer": "We offer a Free plan alongside paid membership tiers with more features. Visit the Membership & Pricing tab for the current plans and what each one includes.",
    "escalate": false
  },
  {
    "category": "Membership & Pricing",
    "question": "What does a membership unlock that a course purchase doesn't?",
    "answer": "Membership unlocks ongoing platform tools like Cheat Sheets, your Verified Badge status, the Load Board, and the Equipment Marketplace — courses are separate, one-time purchases for training content.",
    "escalate": false
  },
  {
    "category": "Membership & Pricing",
    "question": "Is there a free plan?",
    "answer": "Yes, there's a Free plan with limited access — see the Membership & Pricing tab for exactly what's included.",
    "escalate": false
  },
  {
    "category": "Membership & Pricing",
    "question": "Can I cancel my membership anytime?",
    "answer": "Paid membership plans can generally be cancelled anytime — check the specific terms shown for your plan on the Membership & Pricing tab.",
    "escalate": true
  },
  {
    "category": "Membership & Pricing",
    "question": "Does membership pricing vary by account type (Driver/Company/Dispatcher)?",
    "answer": "Some plans are tailored to different account types — for example, company plans include team seats. Check the Membership & Pricing tab for the plan that matches your account type.",
    "escalate": false
  },
  {
    "category": "Membership & Pricing",
    "question": "If I already have a state certification, can I still join as a member?",
    "answer": "Yes — you don't need PPCA to become a member. Upload your existing certification and get verified instantly through the Verification Center.",
    "escalate": false
  },
  {
    "category": "Membership & Pricing",
    "question": "Where can I see exact membership pricing?",
    "answer": "The Membership & Pricing tab always shows current, up-to-date pricing for every plan.",
    "escalate": false
  },
  {
    "category": "Crash Course & Learning Cycle",
    "question": "What is a \"Learning Cycle\"?",
    "answer": "It's the period of continued access to Crash Course materials you get after passing a State Exam and uploading your certification and expiration date — it stays active until your certification expires.",
    "escalate": false
  },
  {
    "category": "Crash Course & Learning Cycle",
    "question": "What is \"Crash Course\" content?",
    "answer": "Refresher material tied to your specific state certification, kept updated as that state's regulations change, available for as long as your Learning Cycle is active.",
    "escalate": false
  },
  {
    "category": "Crash Course & Learning Cycle",
    "question": "How do I activate my Learning Cycle?",
    "answer": "After passing your State Exam, upload your certification documentation and its expiration date — that activates your Learning Cycle automatically.",
    "escalate": false
  },
  {
    "category": "Crash Course & Learning Cycle",
    "question": "What happens when my certification expires?",
    "answer": "Your Crash Course access pauses. Renew your state certification and update your documentation to reactivate the cycle.",
    "escalate": false
  },
  {
    "category": "Crash Course & Learning Cycle",
    "question": "Will I have to retake a quiz during my active Learning Cycle?",
    "answer": "No — since you've already passed the exam, no further quiz retakes are required while your cycle is active.",
    "escalate": false
  },
  {
    "category": "Crash Course & Learning Cycle",
    "question": "How do I renew my Learning Cycle?",
    "answer": "Renew your state certification through your state's process, then upload the updated documentation and new expiration date on PilotLink USA to reactivate your cycle.",
    "escalate": false
  },
  {
    "category": "Crash Course & Learning Cycle",
    "question": "Do I need to re-upload documents every renewal?",
    "answer": "Yes — each time your certification renews, upload the new documentation and expiration date so your Learning Cycle stays active and accurate.",
    "escalate": false
  },
  {
    "category": "Crash Course & Learning Cycle",
    "question": "Does Crash Course content update automatically when state laws change?",
    "answer": "Yes, content is refreshed to reflect current state regulations for anyone with an active Learning Cycle.",
    "escalate": false
  },
  {
    "category": "Platform Features",
    "question": "What is the Live Load Board?",
    "answer": "A real-time feed of oversize load listings you can filter by state, certification type, and pay — apply in one click.",
    "escalate": false
  },
  {
    "category": "Platform Features",
    "question": "How do I apply to a load?",
    "answer": "Find a listing on the Live Load Board that matches your certifications and state, then apply directly with one click — companies can contact you right from your profile.",
    "escalate": false
  },
  {
    "category": "Platform Features",
    "question": "What is the Verified Driver Profile?",
    "answer": "Your profile where you upload certifications, insurance, vehicle, and equipment details — companies can see everything they need to hire you in one place.",
    "escalate": false
  },
  {
    "category": "Platform Features",
    "question": "What is the Equipment Marketplace?",
    "answer": "A marketplace to buy and sell oversize signs, strobes, height poles, and other escort equipment from verified sellers.",
    "escalate": false
  },
  {
    "category": "Platform Features",
    "question": "How many listings can I post for free on the Marketplace?",
    "answer": "New sellers get 20 free listings per month on the Equipment Marketplace.",
    "escalate": false
  },
  {
    "category": "Platform Features",
    "question": "Is there a fee to sell equipment on the Marketplace?",
    "answer": "Your first 20 listings each month are free. Check the Marketplace for current details if you plan to list more.",
    "escalate": false
  },
  {
    "category": "Platform Features",
    "question": "What is the Invoice Builder?",
    "answer": "A tool that generates professional invoices in seconds, auto-calculates mileage, and lets you send them directly to the company you're working for.",
    "escalate": false
  },
  {
    "category": "Platform Features",
    "question": "Can companies see my certifications directly?",
    "answer": "Yes — once verified, your certifications and Verified Badge show directly on your profile for companies to review.",
    "escalate": false
  },
  {
    "category": "Platform Features",
    "question": "Is Eco Back-Haul available yet?",
    "answer": "Not yet — it's planned for a future phase of the platform. We'll announce it here when it launches.",
    "escalate": false
  },
  {
    "category": "Platform Features",
    "question": "What's coming next to the platform?",
    "answer": "We're continually adding features — keep an eye on the Platform Features tab for what's newly available.",
    "escalate": false
  },
  {
    "category": "Account & Technical",
    "question": "How do I create an account?",
    "answer": "Click 'Join Us' in the top navigation, choose your account type (Driver, Company, or Dispatcher), and follow the sign-up steps.",
    "escalate": false
  },
  {
    "category": "Account & Technical",
    "question": "I forgot my password — what do I do?",
    "answer": "Use the 'forgot password' link on the sign-in page to reset it. If you don't receive a reset email, our support team can help.",
    "escalate": true
  },
  {
    "category": "Account & Technical",
    "question": "Can I switch between Driver, Company, and Dispatcher account types?",
    "answer": "Account type is generally tied to how your profile is set up — reach out to support if you need to change your account type.",
    "escalate": true
  },
  {
    "category": "Account & Technical",
    "question": "Does the site work in dark mode?",
    "answer": "Yes — click the moon/sun icon in the top navigation to switch between light and dark mode anytime.",
    "escalate": false
  },
  {
    "category": "Account & Technical",
    "question": "Is my documentation/data secure?",
    "answer": "We take data security seriously, including how certification documents and personal information are stored. If you have specific security or privacy questions, our support team can give you full details.",
    "escalate": true
  },
  {
    "category": "Account & Technical",
    "question": "Can I use PilotLink USA on a tablet or only a phone/computer?",
    "answer": "The site is fully responsive and works on phone, tablet, and desktop.",
    "escalate": false
  },
  {
    "category": "Account & Technical",
    "question": "The site looks broken/a page won't load — what should I do?",
    "answer": "Try refreshing the page or clearing your browser cache first. If the issue continues, our support team can look into it directly.",
    "escalate": true
  },
  {
    "category": "Account & Technical",
    "question": "Do I need to create a new account for each state I certify in?",
    "answer": "No — one account covers all your certifications. You can add certifications for multiple states to the same Verified Driver Profile.",
    "escalate": false
  },
  {
    "category": "Payments & Refunds",
    "question": "What payment methods do you accept?",
    "answer": "Standard major payment methods are accepted at checkout. For a specific payment method question, our support team can confirm what's currently supported.",
    "escalate": true
  },
  {
    "category": "Payments & Refunds",
    "question": "Do you offer refunds?",
    "answer": "No monetary refunds are provided under any circumstances. Instead, the Pass Guarantee lets you retake your exam for free if you complete 100% of the course content and don't pass.",
    "escalate": false
  },
  {
    "category": "Payments & Refunds",
    "question": "What exactly does the Pass Guarantee cover?",
    "answer": "It applies only to enrolled users who complete 100% of course content before taking their PPCA or State Exam — it guarantees free retakes, not a monetary refund.",
    "escalate": false
  },
  {
    "category": "Payments & Refunds",
    "question": "I completed the course but didn't take the exam yet — am I covered by the Pass Guarantee?",
    "answer": "The Pass Guarantee applies once you've completed 100% of the course content and taken the exam. Complete the course, then take your exam to be covered.",
    "escalate": false
  },
  {
    "category": "Payments & Refunds",
    "question": "I was charged twice / charged the wrong amount — what do I do?",
    "answer": "That needs a look at your specific account and payment history, so I'll get this to our support team to sort out for you.",
    "escalate": true
  },
  {
    "category": "Payments & Refunds",
    "question": "Can I get an invoice/receipt for your purchase?",
    "answer": "Yes, receipts are generally available in your account after purchase. If you can't locate yours, our support team can resend it.",
    "escalate": true
  },
  {
    "category": "Escalation",
    "question": "Peter can't answer my question — how do I reach a real person?",
    "answer": "No problem — I'll pass this conversation to our support team so a real person can help you directly.",
    "escalate": true
  },
  {
    "category": "Escalation",
    "question": "My certification documents were flagged as fraudulent/rejected — what happens now?",
    "answer": "Situations involving flagged documentation are handled directly by our support and compliance team, not by me. I'll connect you with them now.",
    "escalate": true
  },
  {
    "category": "Driver Dashboard",
    "question": "What can I do from my Driver Dashboard?",
    "answer": "Your Driver Dashboard is your home base — track your certifications, Verified Badge status, active loads, and messages all in one place.",
    "escalate": "No"
  },
  {
    "category": "Driver Dashboard",
    "question": "Can I see my certification expiration dates on my dashboard?",
    "answer": "Yes, your active certifications and their expiration dates are shown on your dashboard so you always know when it's time to renew.",
    "escalate": "No"
  },
  {
    "category": "Driver Dashboard",
    "question": "How do I update my vehicle or equipment info?",
    "answer": "Update your vehicle and equipment details from your Verified Driver Profile — companies see this information when reviewing you for a job.",
    "escalate": "No"
  },
  {
    "category": "Driver Dashboard",
    "question": "Can I track which loads I've applied to?",
    "answer": "Yes, applied and accepted loads are tracked in your dashboard so you can follow up without digging through messages.",
    "escalate": "No"
  },
  {
    "category": "Driver Dashboard",
    "question": "How do I know if a company has viewed my profile?",
    "answer": "Profile view and interest activity may appear on your dashboard depending on your plan — check your Driver Dashboard for the latest activity.",
    "escalate": "No"
  },
  {
    "category": "Driver Dashboard",
    "question": "Can I set my availability so companies know when I'm free?",
    "answer": "Yes, your Verified Driver Profile includes an availability toggle so companies know when you're open for work.",
    "escalate": "No"
  },
  {
    "category": "Driver Dashboard",
    "question": "Is there a way to message a company directly?",
    "answer": "Yes, once you're connected through a load or the marketplace, you can message directly through the platform's messaging system.",
    "escalate": "No"
  },
  {
    "category": "Driver Dashboard",
    "question": "Can I download a copy of my certifications from my dashboard?",
    "answer": "Certification documents you've uploaded are stored on your profile — for downloading or exporting specific copies, our support team can help.",
    "escalate": "Yes"
  },
  {
    "category": "Company Dashboard",
    "question": "What can a Company account do that a Driver account can't?",
    "answer": "Company accounts can post loads, browse and hire Verified drivers, manage a team, and access hiring analytics — tools built for businesses rather than individual operators.",
    "escalate": "No"
  },
  {
    "category": "Company Dashboard",
    "question": "How do I post a load?",
    "answer": "From your Company Dashboard, use Post Load to create a listing with the route, dimensions, dates, and pay — it goes live on the Load Board immediately.",
    "escalate": "No"
  },
  {
    "category": "Company Dashboard",
    "question": "How do I find certified drivers for a specific state?",
    "answer": "Use the Load Board or driver search filters to browse Verified drivers by state, certification type, and availability.",
    "escalate": "No"
  },
  {
    "category": "Company Dashboard",
    "question": "What is My Loads?",
    "answer": "My Loads shows every load your company has posted, along with its status — open, in progress, or completed.",
    "escalate": "No"
  },
  {
    "category": "Company Dashboard",
    "question": "What does the Analytics page show?",
    "answer": "Analytics gives your company visibility into hiring activity, load performance, and other metrics to help you run your escort operations more efficiently.",
    "escalate": "No"
  },
  {
    "category": "Company Dashboard",
    "question": "Can I manage multiple team members under one Company account?",
    "answer": "Yes, Company plans support team seats so multiple people on your team can manage loads and hiring from one account.",
    "escalate": "No"
  },
  {
    "category": "Company Dashboard",
    "question": "How do I verify a driver's certification before hiring them?",
    "answer": "Every Verified driver's profile shows their certification status and Verified Badge with a scannable QR code — you can confirm it's current before hiring.",
    "escalate": "No"
  },
  {
    "category": "Company Dashboard",
    "question": "Can I edit or remove a load after posting it?",
    "answer": "Yes, loads you've posted can be edited or removed from My Loads while they're still open.",
    "escalate": "No"
  },
  {
    "category": "Company Dashboard",
    "question": "How do I contact a driver who applied to my load?",
    "answer": "Once a driver applies, you can message them directly through the platform to coordinate details.",
    "escalate": "No"
  },
  {
    "category": "Company Dashboard",
    "question": "Is there a limit to how many loads a company can post?",
    "answer": "Load posting limits, if any, depend on your membership plan — check the Membership & Pricing tab for what's included with your plan.",
    "escalate": "No"
  },
  {
    "category": "Dispatcher Tools",
    "question": "What is the Dispatcher Console?",
    "answer": "A dedicated workspace for dispatchers to coordinate pilot car escorts across active loads, keeping drivers, companies, and routes organized in one view.",
    "escalate": "No"
  },
  {
    "category": "Dispatcher Tools",
    "question": "What can a Dispatcher account do?",
    "answer": "Dispatcher accounts can view and coordinate loads, communicate with drivers and companies, and help manage escort logistics without needing a full Company account.",
    "escalate": "No"
  },
  {
    "category": "Dispatcher Tools",
    "question": "Do dispatchers need to be certified themselves?",
    "answer": "No, certification applies to pilot car drivers performing the escort — dispatchers coordinating the logistics don't need a driver certification.",
    "escalate": "No"
  },
  {
    "category": "Dispatcher Tools",
    "question": "Can a dispatcher message drivers directly?",
    "answer": "Yes, dispatchers can message drivers and companies through the platform's messaging tools to coordinate a job.",
    "escalate": "No"
  },
  {
    "category": "Dispatcher Tools",
    "question": "How is the Dispatcher Dashboard different from the Console?",
    "answer": "The Dashboard gives a broader overview of your dispatch activity, while the Console is focused on managing the details of active loads.",
    "escalate": "No"
  },
  {
    "category": "Dispatcher Tools",
    "question": "Can dispatchers see driver certification status?",
    "answer": "Yes, Verified driver certifications and badges are visible to dispatchers coordinating a job, just like they are to companies.",
    "escalate": "No"
  },
  {
    "category": "Dispatcher Tools",
    "question": "Do dispatchers have their own login?",
    "answer": "Yes, dispatchers sign in through a dedicated Dispatcher login separate from Driver and Company accounts.",
    "escalate": "No"
  },
  {
    "category": "Dispatcher Tools",
    "question": "Can a dispatcher work with more than one company?",
    "answer": "Account setup for working across multiple companies is something our support team can help configure for your situation.",
    "escalate": "Yes"
  },
  {
    "category": "Crash Course",
    "question": "What's the difference between the course I buy and the Crash Course?",
    "answer": "The course you purchase is your full certification training. Crash Course is the ongoing refresher content tied to your active Learning Cycle after you've passed your exam.",
    "escalate": "No"
  },
  {
    "category": "Crash Course",
    "question": "Is there a Crash Course for all 50 states?",
    "answer": "Yes, our All-States Crash Course covers refresher material across every state's requirements, alongside more focused options for specific state groups.",
    "escalate": "No"
  },
  {
    "category": "Crash Course",
    "question": "What is the Washington States Crash Course?",
    "answer": "A refresher course focused specifically on Washington's escort and certification requirements, for drivers whose Learning Cycle centers on that state.",
    "escalate": "No"
  },
  {
    "category": "Crash Course",
    "question": "How do I access my Crash Course content?",
    "answer": "Once your Learning Cycle is active, your Crash Course materials are available from your training area — look for the Crash Course section under Training.",
    "escalate": "No"
  },
  {
    "category": "Crash Course",
    "question": "Is Crash Course content the same as the original course modules?",
    "answer": "No, Crash Course content is shorter, refresher-focused material meant to keep you current, not a repeat of the full original course.",
    "escalate": "No"
  },
  {
    "category": "Crash Course",
    "question": "What is the State Infographic in Crash Course?",
    "answer": "A visual, at-a-glance summary of a state's key rules — useful for a fast refresher without reading through full text modules.",
    "escalate": "No"
  },
  {
    "category": "Crash Course",
    "question": "What is Post-Exam Verification?",
    "answer": "The step where you confirm and submit your passing exam results and certification documentation so your Verified Badge or Learning Cycle can be activated.",
    "escalate": "No"
  },
  {
    "category": "Crash Course",
    "question": "Can I access Crash Course content on my phone?",
    "answer": "Yes, Crash Course pages are fully responsive and work on phone, tablet, or desktop.",
    "escalate": "No"
  },
  {
    "category": "Crash Course",
    "question": "Do I lose my Crash Course progress if my certification expires?",
    "answer": "Your progress isn't lost, but active access pauses when your certification expires — renewing your certification reactivates your Learning Cycle.",
    "escalate": "No"
  },
  {
    "category": "Crash Course",
    "question": "How do I know which Crash Course applies to me?",
    "answer": "It's based on which certification path you completed — your training area will show the Crash Course tied to your specific certification.",
    "escalate": "No"
  },
  {
    "category": "Verified Badge",
    "question": "Where does my Verified Badge show up?",
    "answer": "Your Verified Badge appears on your Driver Profile, visible to any company or dispatcher reviewing you for a job.",
    "escalate": "No"
  },
  {
    "category": "Verified Badge",
    "question": "Can a company scan my badge in person?",
    "answer": "Yes, your badge includes a unique QR code that can be scanned to instantly verify your certification status.",
    "escalate": "No"
  },
  {
    "category": "Verified Badge",
    "question": "Does my badge expire?",
    "answer": "For PPCA, once issued your badge doesn't require further quiz retakes. For State Exam certifications, badge/Learning Cycle status is tied to your certification's expiration date.",
    "escalate": "No"
  },
  {
    "category": "Verified Badge",
    "question": "What happens to my badge if my certification lapses?",
    "answer": "If your state certification expires without renewal, your badge status will reflect that until you upload updated documentation.",
    "escalate": "No"
  },
  {
    "category": "Verified Badge",
    "question": "Can I have more than one badge for different certifications?",
    "answer": "Yes, your profile can display multiple certifications and their statuses if you're certified through more than one path.",
    "escalate": "No"
  },
  {
    "category": "Verified Badge",
    "question": "Is the badge visible to companies before they contact me?",
    "answer": "Yes, your Verified Badge and certification status are visible on your public driver profile.",
    "escalate": "No"
  },
  {
    "category": "Verified Badge",
    "question": "How long does it take to get my badge after passing?",
    "answer": "For PPCA, it's automatic and near-instant after your results are transmitted. For State Exam certifications, it activates once you upload your documentation.",
    "escalate": "No"
  },
  {
    "category": "Verified Badge",
    "question": "Can I dispute a badge status I think is wrong?",
    "answer": "Yes — if you believe your badge status is incorrect, our support team can review your certification records and correct it.",
    "escalate": "Yes"
  },
  {
    "category": "TradeLink & Messaging",
    "question": "What is TradeLink?",
    "answer": "TradeLink is the messaging and negotiation system tied to Equipment Marketplace listings, so buyers and sellers can coordinate deals directly.",
    "escalate": "No"
  },
  {
    "category": "TradeLink & Messaging",
    "question": "How do I message a seller about a listing?",
    "answer": "Open the listing in the Equipment Marketplace and use TradeLink to send the seller a message directly.",
    "escalate": "No"
  },
  {
    "category": "TradeLink & Messaging",
    "question": "Where do I find my messages?",
    "answer": "All your platform messages — load-related, marketplace, and general — are available from the Messages section of your account.",
    "escalate": "No"
  },
  {
    "category": "TradeLink & Messaging",
    "question": "Can I negotiate price through TradeLink?",
    "answer": "Yes, TradeLink is built for back-and-forth conversations on a listing, including price negotiation between buyer and seller.",
    "escalate": "No"
  },
  {
    "category": "TradeLink & Messaging",
    "question": "Is TradeLink only for buying and selling equipment?",
    "answer": "TradeLink is focused on Marketplace listings; general platform messaging with drivers, companies, and dispatchers happens through the main Messages inbox.",
    "escalate": "No"
  },
  {
    "category": "TradeLink & Messaging",
    "question": "Can I post a listing directly from TradeLink?",
    "answer": "You post a listing through the Equipment Marketplace's Post Listing flow — TradeLink handles the conversation once buyers reach out.",
    "escalate": "No"
  },
  {
    "category": "TradeLink & Messaging",
    "question": "Do I get notified when I receive a new message?",
    "answer": "Yes, you'll be notified of new messages so you don't miss a conversation about a load or a listing.",
    "escalate": "No"
  },
  {
    "category": "TradeLink & Messaging",
    "question": "Is there a fee to use TradeLink messaging?",
    "answer": "Messaging itself isn't a separate paid feature — it's part of using the Marketplace and platform messaging tools.",
    "escalate": "No"
  },
  {
    "category": "Legal & Policies",
    "question": "Where can I find your Privacy Policy?",
    "answer": "Our full Privacy Policy is linked in the site footer and available at the /privacy page.",
    "escalate": "No"
  },
  {
    "category": "Legal & Policies",
    "question": "Where can I read your Terms of Service?",
    "answer": "Our Terms of Service are available at the /terms page, linked in the site footer.",
    "escalate": "No"
  },
  {
    "category": "Legal & Policies",
    "question": "What's your Refund Policy?",
    "answer": "PilotLink USA does not provide monetary refunds under any circumstances — course purchases are covered instead by the Pass Guarantee, which offers free retakes. Full details are on the Refund Policy page.",
    "escalate": "No"
  },
  {
    "category": "Legal & Policies",
    "question": "How do I contact PilotLink USA directly?",
    "answer": "Visit our Contact Us page, or ask me and I'll route you to the right person if I can't answer directly.",
    "escalate": "No"
  },
  {
    "category": "Legal & Policies",
    "question": "Does PilotLink USA sell my personal data?",
    "answer": "Specific data-handling practices are detailed in our Privacy Policy — for a precise answer about your data, our support team can walk you through it.",
    "escalate": "Yes"
  },
  {
    "category": "Legal & Policies",
    "question": "Are your courses legally recognized by every state DOT?",
    "answer": "We build our courses around each state's published requirements, but we don't issue official state licenses ourselves — always confirm current requirements with your state's DOT.",
    "escalate": "No"
  },
  {
    "category": "Legal & Policies",
    "question": "What happens to my account if I violate your Terms of Service?",
    "answer": "Violations of our Terms of Service can result in suspension or termination of platform access — see the full Terms of Service page for details.",
    "escalate": "No"
  },
  {
    "category": "Legal & Policies",
    "question": "Is PilotLink USA insured or liable for jobs I take through the platform?",
    "answer": "PilotLink USA is a training and connection platform — liability for jobs taken through the platform is addressed in our Terms of Service. For specifics, our support team can help.",
    "escalate": "Yes"
  },
  {
    "category": "Legal & Policies",
    "question": "Can I request a copy of my personal data?",
    "answer": "Data requests like this should go to our support team so they can be handled properly and securely.",
    "escalate": "Yes"
  },
  {
    "category": "Legal & Policies",
    "question": "Do you use cookies on your site?",
    "answer": "Details on cookies and tracking are covered in our Privacy Policy.",
    "escalate": "No"
  },
  {
    "category": "Account Types & Login",
    "question": "How do I log in as a Driver?",
    "answer": "Use the Driver login page and sign in with the email and password you used to sign up.",
    "escalate": "No"
  },
  {
    "category": "Account Types & Login",
    "question": "How do I log in as a Company?",
    "answer": "Companies have a dedicated Company login page, separate from the Driver login.",
    "escalate": "No"
  },
  {
    "category": "Account Types & Login",
    "question": "How do I log in as a Dispatcher?",
    "answer": "Dispatchers have their own dedicated Dispatcher login page, separate from Driver and Company logins.",
    "escalate": "No"
  },
  {
    "category": "Account Types & Login",
    "question": "Can I have both a Driver and a Company account?",
    "answer": "Each account type is set up separately. If you need both, reach out to our support team to get that configured correctly.",
    "escalate": "Yes"
  },
  {
    "category": "Account Types & Login",
    "question": "What's the difference in sign-up between a Driver and a Company?",
    "answer": "Driver sign-up focuses on your certifications and profile; Company sign-up focuses on your business details for posting loads and hiring.",
    "escalate": "No"
  },
  {
    "category": "Account Types & Login",
    "question": "Can I upgrade from a Driver account to a Company account?",
    "answer": "Changing account types isn't a simple self-service toggle — our support team can help you set up the right account for your needs.",
    "escalate": "Yes"
  },
  {
    "category": "Account Types & Login",
    "question": "Do I need a credit card to sign up?",
    "answer": "Signing up for a Free account or plan doesn't require payment info — you'll only need a payment method when purchasing a course or upgrading to a paid plan.",
    "escalate": "No"
  },
  {
    "category": "Account Types & Login",
    "question": "Is there a mobile app, or is it web-only?",
    "answer": "PilotLink USA is a fully responsive website that works great on mobile browsers — check the site for the latest on any dedicated app.",
    "escalate": "No"
  },
  {
    "category": "Account Types & Login",
    "question": "Can I sign up without an email address?",
    "answer": "An email address is required to create and secure your account.",
    "escalate": "No"
  },
  {
    "category": "Account Types & Login",
    "question": "How do I become a partner with PilotLink USA?",
    "answer": "Visit our Become a Partner page to learn more and submit your interest.",
    "escalate": "No"
  },
  {
    "category": "Passwords & Security",
    "question": "How do I reset my password?",
    "answer": "Use the 'Forgot Password' link on the login page — we'll send you a reset link by email.",
    "escalate": "No"
  },
  {
    "category": "Passwords & Security",
    "question": "I didn't receive my password reset email — what do I do?",
    "answer": "Check your spam folder first. If it's still missing after a few minutes, our support team can help resend it.",
    "escalate": "Yes"
  },
  {
    "category": "Passwords & Security",
    "question": "How do I change my password once I'm logged in?",
    "answer": "Password changes are managed from your account settings once you're logged in.",
    "escalate": "No"
  },
  {
    "category": "Passwords & Security",
    "question": "Is two-factor authentication available?",
    "answer": "For specifics on account security features currently available, our support team can give you the most current details.",
    "escalate": "Yes"
  },
  {
    "category": "Passwords & Security",
    "question": "What should I do if I think my account was accessed without my permission?",
    "answer": "Reset your password immediately and contact our support team so we can help secure your account.",
    "escalate": "Yes"
  },
  {
    "category": "Passwords & Security",
    "question": "How long does a password reset link stay valid?",
    "answer": "Reset links are time-limited for security. If yours has expired, just request a new one from the login page.",
    "escalate": "No"
  },
  {
    "category": "Partnerships",
    "question": "What does becoming a PilotLink USA partner mean?",
    "answer": "Partnership opportunities can include referral relationships, integrations, or business collaborations — details are on our Become a Partner page.",
    "escalate": "No"
  },
  {
    "category": "Partnerships",
    "question": "Who can apply to become a partner?",
    "answer": "Companies, trainers, and organizations in the oversize load and logistics space are welcome to apply through the Become a Partner page.",
    "escalate": "No"
  },
  {
    "category": "Partnerships",
    "question": "Is there a cost to become a partner?",
    "answer": "Partnership terms vary by the type of partnership — our team can walk you through details after you submit interest.",
    "escalate": "Yes"
  },
  {
    "category": "Partnerships",
    "question": "How long does the partner application process take?",
    "answer": "Review timelines can vary — after you submit your interest, our team will follow up with next steps.",
    "escalate": "Yes"
  },
  {
    "category": "Partnerships",
    "question": "Can training providers partner with PilotLink USA?",
    "answer": "Yes, training and certification-related partnerships are something we're open to discussing — start with the Become a Partner page.",
    "escalate": "No"
  },
  {
    "category": "Partnerships",
    "question": "Who do I contact about a partnership opportunity?",
    "answer": "Submit your details on the Become a Partner page and our team will follow up directly.",
    "escalate": "Yes"
  },
  {
    "category": "Mobile & Notifications",
    "question": "Does the site work well on my phone?",
    "answer": "Yes, PilotLink USA is fully responsive and designed to work smoothly on phones, tablets, and desktops alike.",
    "escalate": "No"
  },
  {
    "category": "Mobile & Notifications",
    "question": "Will I get notified about new loads that match me?",
    "answer": "Yes, real-time notifications alert you to new load listings that match your certifications and filters.",
    "escalate": "No"
  },
  {
    "category": "Mobile & Notifications",
    "question": "Can I turn off notifications?",
    "answer": "Notification preferences are managed in your account settings.",
    "escalate": "No"
  },
  {
    "category": "Mobile & Notifications",
    "question": "Does dark mode work on mobile too?",
    "answer": "Yes, dark mode works the same across desktop, tablet, and mobile — just toggle it from the navigation menu.",
    "escalate": "No"
  },
  {
    "category": "Mobile & Notifications",
    "question": "Will I be notified when my certification is about to expire?",
    "answer": "Expiration reminders help make sure you renew on time — check your account notification settings for how these are delivered.",
    "escalate": "No"
  },
  {
    "category": "Mobile & Notifications",
    "question": "Can I get notified about new equipment listings in the Marketplace?",
    "answer": "Marketplace notification preferences can be managed from your account settings.",
    "escalate": "No"
  },
  {
    "category": "General & Troubleshooting",
    "question": "Why can't I see the Cheat Sheets even though I'm a member?",
    "answer": "Make sure you're logged into the account with your active membership — if the issue continues, our support team can check your account.",
    "escalate": "Yes"
  },
  {
    "category": "General & Troubleshooting",
    "question": "My payment didn't go through — what should I do?",
    "answer": "Double-check your payment details and try again. If it still fails, our support team can look into what happened.",
    "escalate": "Yes"
  },
  {
    "category": "General & Troubleshooting",
    "question": "I enrolled in the wrong course — can I switch?",
    "answer": "Reach out to our support team with your order details and they'll help sort out the right course for you.",
    "escalate": "Yes"
  },
  {
    "category": "General & Troubleshooting",
    "question": "How do I delete my account?",
    "answer": "Account deletion requests are handled by our support team to make sure it's done correctly and securely.",
    "escalate": "Yes"
  },
  {
    "category": "General & Troubleshooting",
    "question": "Can I use PilotLink USA outside the United States?",
    "answer": "PilotLink USA is built around U.S. state certification requirements — for questions about use outside the U.S., our support team can advise.",
    "escalate": "Yes"
  },
  {
    "category": "General & Troubleshooting",
    "question": "Is customer support available 24/7?",
    "answer": "I'm available anytime to answer common questions instantly. For anything I hand off, our human support team typically replies within 24 hours.",
    "escalate": "No"
  },
  {
    "category": "General & Troubleshooting",
    "question": "How do I leave feedback about the platform?",
    "answer": "We'd love to hear it — send feedback through the Contact Us page or let me know and I'll pass it along.",
    "escalate": "No"
  },
  {
    "category": "General & Troubleshooting",
    "question": "Can I use the same account on multiple devices?",
    "answer": "Yes, just log in with your credentials on any device — your account and progress stay in sync.",
    "escalate": "No"
  },
  {
    "category": "General & Troubleshooting",
    "question": "What browsers does the site support?",
    "answer": "The site is built to work on all modern browsers — Chrome, Safari, Firefox, and Edge.",
    "escalate": "No"
  },
  {
    "category": "General & Troubleshooting",
    "question": "I have a question that's not listed here — what now?",
    "answer": "Type it in below and I'll do my best to match it to something I know. If I can't, I'll connect you with our support team.",
    "escalate": "No"
  }
];

const QUICK_QUESTIONS = [
  "What is PilotLink USA?",
  "How much do the courses cost?",
  "What is the Verified Badge?",
  "Do I need certification in my state?",
  "What's the Pass Guarantee?",
];

const STOPWORDS = new Set(["what","is","the","a","an","do","does","i","my","to","for","of","in","on","and","or","are","can","how","if","it","this","that","you","your","need","about"]);

function tokenize(str) {
  return (str || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(w => w && !STOPWORDS.has(w));
}

function findBestAnswer(query) {
  const qTokens = tokenize(query);
  if (qTokens.length === 0) return null;
  let best = null;
  let bestScore = 0;
  for (const item of QA_DATA) {
    const qWords = tokenize(item.question);
    const aWords = tokenize(item.answer);
    const cWords = tokenize(item.category);
    let score = 0;
    for (const t of qTokens) {
      if (qWords.includes(t)) score += 3;
      if (cWords.includes(t)) score += 2;
      if (aWords.includes(t)) score += 1;
    }
    if (score > bestScore) { bestScore = score; best = item; }
  }
  return bestScore >= 3 ? best : null;
}

// ── BACKEND HOOK #1: called once, right after the lead form is submitted.
// Currently just persists to localStorage. Replace/extend with a real POST,
// e.g. fetch("/api/peter/leads", { method:"POST", body: JSON.stringify(lead) }).
function submitLead(lead) {
  try { localStorage.setItem(LEAD_STORAGE_KEY, JSON.stringify(lead)); } catch (e) { /* storage unavailable */ }
}

// ── BACKEND HOOK #2: called every time a question is asked (quick-reply or
// typed). Currently appends to a local log array in localStorage so you can
// see it's working. Replace/extend with a real POST, e.g.
// fetch("/api/peter/log", { method:"POST", body: JSON.stringify(entry) }).
function logInteraction(lead, entry) {
  try {
    const existing = JSON.parse(localStorage.getItem(LOG_STORAGE_KEY) || "[]");
    existing.push({ ...entry, lead: lead ? { email: lead.email } : null, at: new Date().toISOString() });
    localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(existing));
  } catch (e) { /* storage unavailable */ }
}

function loadStoredLead() {
  try {
    const raw = localStorage.getItem(LEAD_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
}

const THEME = {
  light: {
    navy: "#0d1f3c", navyMid: "#1a3358",
    gold: "#c9971c", goldSoft: "#fef9ec",
    surface: "#ffffff", surfaceAlt: "#f8fafc",
    text: "#0d1f3c", textMid: "#334155", textSub: "#64748b",
    border: "#e2e8f0", bubbleBot: "#f1f5f9",
    amber: "#b45309", amberBg: "#fffbeb", amberBorder: "#fde68a",
    inputBg: "#ffffff",
  },
  dark: {
    navy: "#0a1628", navyMid: "#0d1f3c",
    gold: "#fbbf24", goldSoft: "rgba(251,191,36,0.12)",
    surface: "#142a4d", surfaceAlt: "#0d1f3c",
    text: "#f0f4f8", textMid: "rgba(240,244,248,0.85)", textSub: "rgba(240,244,248,0.60)",
    border: "rgba(255,255,255,0.12)", bubbleBot: "#1a3358",
    amber: "#fbbf24", amberBg: "rgba(251,191,36,0.10)", amberBorder: "rgba(251,191,36,0.35)",
    inputBg: "#0d1f3c",
  },
};

// Detects the site's dark mode by watching the `dark` class on <html>,
// which is exactly what App.jsx's toggleTheme()/useEffect already applies.
function useAutoDarkMode(forced) {
  const [isDark, setIsDark] = useState(() => {
    if (typeof forced === "boolean") return forced;
    if (typeof document === "undefined") return false;
    return document.documentElement.classList.contains("dark");
  });

  useEffect(() => {
    if (typeof forced === "boolean") { setIsDark(forced); return; }
    if (typeof document === "undefined") return;
    const update = () => setIsDark(document.documentElement.classList.contains("dark"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [forced]);

  return isDark;
}

function Avatar({ size = 34 }) {
  return (
    <div style={{ width:size, height:size, borderRadius:"50%", background:"linear-gradient(135deg,#c9971c,#fde68a)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:size*0.5, flexShrink:0 }} aria-hidden="true">
      🤖
    </div>
  );
}

function BotBubble({ children, C }) {
  return (
    <div style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:14 }}>
      <Avatar size={28} />
      <div style={{ background:C.bubbleBot, color:C.text, borderRadius:"4px 16px 16px 16px", padding:"12px 14px", fontSize:13.5, lineHeight:1.6, maxWidth:"78%" }}>
        {children}
      </div>
    </div>
  );
}

function UserBubble({ children, C }) {
  return (
    <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:14 }}>
      <div style={{ background:C.navy, color:"#fff", borderRadius:"16px 4px 16px 16px", padding:"11px 16px", fontSize:13.5, lineHeight:1.5, maxWidth:"78%", fontWeight:600 }}>
        {children}
      </div>
    </div>
  );
}

function EscalateCard({ C }) {
  return (
    <div style={{ background:C.amberBg, border:`1px solid ${C.amberBorder}`, borderRadius:12, padding:"12px 14px", marginLeft:38, marginTop:-6, marginBottom:14 }}>
      <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
        <span aria-hidden="true">🤝</span>
        <span style={{ fontSize:12.5, fontWeight:700, color:C.amber }}>This one needs a real person</span>
      </div>
      <p style={{ fontSize:12, color:C.textMid, lineHeight:1.6, margin:"0 0 10px" }}>
        I'll pass this to our support team — they typically reply within 24 hours.
      </p>
      <a
        href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Support request from PilotLink USA site")}`}
        style={{ display:"inline-flex", alignItems:"center", gap:6, background:C.navy, color:"#fff", fontSize:12.5, fontWeight:700, padding:"8px 14px", borderRadius:99, textDecoration:"none" }}
      >
        ✉️ Email {SUPPORT_EMAIL}
      </a>
    </div>
  );
}

// ── Minimum KYC gate: first name, last name, email, phone — required before
// the chat opens up, so every conversation can be tied back to a real lead.
function LeadForm({ C, onSubmit }) {
  const [form, setForm] = useState({ firstName:"", lastName:"", email:"", phone:"" });
  const [touched, setTouched] = useState(false);

  const errors = useMemo(() => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (form.phone.replace(/\D/g, "").length < 10) e.phone = "Enter a valid phone number";
    return e;
  }, [form]);

  const isValid = Object.keys(errors).length === 0;

  const field = (key, label, placeholder, type = "text") => (
    <div style={{ marginBottom:12 }}>
      <label style={{ display:"block", fontSize:11.5, fontWeight:700, color:C.textSub, marginBottom:5 }}>{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        style={{
          width:"100%", padding:"10px 12px", borderRadius:10, fontSize:13.5, boxSizing:"border-box",
          border:`1.5px solid ${touched && errors[key] ? "#dc2626" : C.border}`,
          background:C.inputBg, color:C.text, outline:"none",
        }}
      />
      {touched && errors[key] && <div style={{ fontSize:11, color:"#dc2626", marginTop:3 }}>{errors[key]}</div>}
    </div>
  );

  return (
    <div style={{ padding:"20px 18px", flex:1, overflowY:"auto" }}>
      <div style={{ textAlign:"center", marginBottom:18 }}>
        <Avatar size={44} />
        <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:16, color:C.text, marginTop:10 }}>Before we start chatting…</div>
        <p style={{ fontSize:12.5, color:C.textSub, lineHeight:1.6, margin:"6px 0 0" }}>
          Just a few quick details so Peter (and our team, if you need them) knows who you are.
        </p>
      </div>

      {field("firstName", "First name", "Jordan")}
      {field("lastName", "Last name", "Rivera")}
      {field("email", "Email", "jordan@example.com", "email")}
      {field("phone", "Phone number", "(555) 123-4567", "tel")}

      <button
        onClick={() => { setTouched(true); if (isValid) onSubmit(form); }}
        style={{
          width:"100%", padding:"12px", borderRadius:10, border:"none", marginTop:6,
          background: C.gold, color:"#0d1f3c", fontWeight:800, fontSize:13.5, cursor:"pointer",
        }}
      >
        Start chatting with Peter →
      </button>
      <p style={{ fontSize:10.5, color:C.textSub, textAlign:"center", marginTop:10, lineHeight:1.5 }}>
        We'll only use this to follow up if you need help beyond what Peter can answer.
      </p>
    </div>
  );
}

export default function PeterQAWidget({ isDarkMode }) {
  const isDark = useAutoDarkMode(isDarkMode);
  const C = isDark ? THEME.dark : THEME.light;

  const [open, setOpen] = useState(false);
  const [lead, setLead] = useState(null);
  const [leadChecked, setLeadChecked] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [askedOnce, setAskedOnce] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    setLead(loadStoredLead());
    setLeadChecked(true);
  }, []);

  useEffect(() => {
    if (open && lead && messages.length === 0) {
      setMessages([
        { from: "bot", text: `Hi ${lead.firstName} 👋 I'm Peter — I can answer most questions about certification, courses, pricing, and the platform, right now.` },
        { from: "bot", text: "Try one of these, or type your own question below:", quickReplies: true },
      ]);
    }
  }, [open, lead]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const handleLeadSubmit = (form) => {
    const newLead = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
    };
    submitLead(newLead);
    setLead(newLead);
  };

  const handleAsk = (rawText) => {
    const text = rawText.trim();
    if (!text) return;
    const match = findBestAnswer(text);
    setMessages(prev => [
      ...prev,
      { from: "user", text },
      match
        ? { from: "bot", text: match.answer, escalate: match.escalate }
        : { from: "bot", text: "I don't have a ready answer for that one yet.", escalate: true },
    ]);
    logInteraction(lead, { question: text, matchedCategory: match ? match.category : null, escalate: match ? match.escalate : true });
    setInput("");
    setAskedOnce(true);
  };

  return (
    <div className="peter-widget-wrapper" style={{ position:"fixed", bottom:24, right:24, zIndex:9999, fontFamily:"'Inter',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@700;800;900&display=swap');
        .peter-panel { animation: peterIn .18s ease-out; }
        @keyframes peterIn { from { opacity:0; transform: translateY(12px) scale(0.98); } to { opacity:1; transform: translateY(0) scale(1); } }
        .peter-scroll::-webkit-scrollbar { width: 6px; }
        .peter-scroll::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 99px; }
        .peter-chip:hover, .peter-launcher:hover { filter: brightness(1.05); }
        button:focus-visible, input:focus-visible, a:focus-visible { outline: 2px solid #c9971c; outline-offset: 2px; }
        @media (max-width: 480px) {
          /* Shrink the wrapper's own edge offsets so launcher + panel move
             together and stay aligned to the same right edge — the panel
             just inherits right:0 from its base style, so it's centered
             under the launcher with equal margin on both sides, instead of
             being nudged off to one side. */
          .peter-widget-wrapper { right: 12px !important; bottom: 16px !important; }
          .peter-panel { width: calc(100vw - 24px) !important; height: 72vh !important; }
        }
      `}</style>

      {open && leadChecked && (
        <div className="peter-panel" style={{
          width:370, height:540, maxHeight:"78vh", background:C.surface, borderRadius:20,
          boxShadow:"0 20px 50px rgba(13,31,60,0.35)", border:`1px solid ${C.border}`,
          display:"flex", flexDirection:"column", overflow:"hidden", marginBottom:16,
          position:"absolute", bottom:"100%", right:0,
        }}>
          {/* Header */}
          <div style={{ background:"linear-gradient(135deg, #0d1f3c, #1a3358)", padding:"16px 18px", display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
            <Avatar size={38} />
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontWeight:800, fontSize:15, color:"#fff" }}>Peter</div>
              <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:11.5, color:"rgba(255,255,255,0.7)" }}>
                <span style={{ width:7, height:7, borderRadius:"50%", background:"#34d399", display:"inline-block" }} aria-hidden="true" />
                Online now
              </div>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat" style={{ background:"rgba(255,255,255,0.1)", border:"none", color:"#fff", width:28, height:28, borderRadius:8, cursor:"pointer", fontSize:15, lineHeight:1 }}>✕</button>
          </div>

          {!lead ? (
            <LeadForm C={C} onSubmit={handleLeadSubmit} />
          ) : (
            <>
              {/* Messages */}
              <div ref={scrollRef} className="peter-scroll" style={{ flex:1, overflowY:"auto", padding:"18px 16px 8px", background:C.surfaceAlt }}>
                {messages.map((m, i) => {
                  if (m.from === "bot" && m.quickReplies) {
                    return (
                      <React.Fragment key={i}>
                        <BotBubble C={C}>{m.text}</BotBubble>
                        {!askedOnce && (
                          <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginLeft:38, marginTop:-6, marginBottom:16 }}>
                            {QUICK_QUESTIONS.map(q => (
                              <button key={q} className="peter-chip" onClick={() => handleAsk(q)} style={{
                                border:`1.5px solid ${C.gold}`, background:C.goldSoft, color: isDark ? C.gold : "#92700f",
                                borderRadius:99, padding:"7px 13px", fontSize:12, fontWeight:700, cursor:"pointer",
                              }}>
                                {q}
                              </button>
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  }
                  if (m.from === "bot") {
                    return (
                      <React.Fragment key={i}>
                        <BotBubble C={C}>{m.text}</BotBubble>
                        {m.escalate && <EscalateCard C={C} />}
                      </React.Fragment>
                    );
                  }
                  return <UserBubble key={i} C={C}>{m.text}</UserBubble>;
                })}
              </div>

              {/* Input */}
              <div style={{ borderTop:`1px solid ${C.border}`, padding:"10px 12px", display:"flex", gap:8, alignItems:"center", background:C.surface, flexShrink:0 }}>
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") handleAsk(input); }}
                  placeholder="Ask Peter a question…"
                  aria-label="Message Peter"
                  style={{ flex:1, border:`1.5px solid ${C.border}`, borderRadius:99, padding:"10px 14px", fontSize:13, outline:"none", background:C.inputBg, color:C.text }}
                />
                <button onClick={() => handleAsk(input)} aria-label="Send message" style={{
                  background:C.gold, color:"#0d1f3c", border:"none", width:38, height:38, borderRadius:"50%",
                  cursor:"pointer", fontSize:15, flexShrink:0,
                }}>➤</button>
              </div>
              <div style={{ textAlign:"center", fontSize:10.5, color:C.textSub, padding:"6px 0 10px", background:C.surface, flexShrink:0 }}>
                Still stuck? Peter can connect you to <strong>{SUPPORT_EMAIL}</strong> · replies within 24 hrs
              </div>
            </>
          )}
        </div>
      )}

      {/* Launcher button */}
      <button
        className="peter-launcher"
        onClick={() => setOpen(o => !o)}
        aria-label={open ? "Close chat with Peter" : "Chat with Peter"}
        style={{
          width:60, height:60, borderRadius:"50%", border:"none", cursor:"pointer",
          background:"linear-gradient(135deg, #0d1f3c, #1a3358)", boxShadow:"0 10px 28px rgba(13,31,60,0.35)",
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:26,
          position:"relative", float:"right",
        }}
      >
        {open ? "✕" : "💬"}
        {!open && (
          <span style={{ position:"absolute", top:-2, right:-2, width:14, height:14, borderRadius:"50%", background:"#34d399", border:"2px solid #fff" }} aria-hidden="true" />
        )}
      </button>
    </div>
  );
}
